import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import './AdminMessagesPage.css';
import {
    Envelope,
    CheckCircle,
    Clock,
    PaperPlaneRight,
    User,
    CaretRight,
    ArrowLeft,
    ArrowClockwise
} from '@phosphor-icons/react';

const AdminMessagesPage = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [filter, setFilter] = useState('ALL'); // ALL, NEW, REPLIED
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async (silent = false) => {
        if (!silent) setIsRefreshing(true);
        try {
            const response = await api.get('/admin/contact/messages');
            setMessages(response.data);
        } catch (error) {
            console.error("Error fetching messages:", error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;

        setIsSending(true);
        try {
            const response = await api.post(`/admin/contact/messages/${selectedMessage.id}/reply`, {
                response: replyText
            });

            if (response.status === 200 || response.status === 201) {
                // Update local state
                const updatedMessages = messages.map(msg =>
                    msg.id === selectedMessage.id
                        ? { ...msg, status: 'REPLIED', response: replyText, responded_at: new Date().toISOString() }
                        : msg
                );
                setMessages(updatedMessages);
                setSelectedMessage({ ...selectedMessage, status: 'REPLIED', response: replyText, responded_at: new Date().toISOString() });
                setReplyText('');
            }
        } catch (error) {
            console.error("Error sending reply:", error);
        } finally {
            setIsSending(false);
        }
    };

    const getFilteredMessages = () => {
        if (filter === 'NEW') return messages.filter(m => m.status === 'NEW');
        if (filter === 'REPLIED') return messages.filter(m => m.status === 'REPLIED');
        return messages;
    };

    return (
        <div className="admin-messages-layout">
            <header className="admin-messages-topbar">
                <div className="topbar-title"><h1>Messages Management</h1></div>
                <div className="topbar-actions">
                    <button
                        onClick={() => fetchMessages()}
                        className={`admin-refresh-btn ${isRefreshing ? 'button-loading' : ''}`}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? 'Syncing...' : <><ArrowClockwise size={18} weight="bold" /> Refresh Data</>}
                    </button>
                    <Link to="/admin-dashboard" className="admin-dashboard-btn">
                        <ArrowLeft size={18} style={{ marginRight: '8px' }} weight="bold" /> Admin Dashboard
                    </Link>
                </div>
            </header>

            <div className="admin-messages-container">
                <div className="messages-sidebar">
                    <div className="sidebar-header">
                        <h2>Messages</h2>
                        <div className="filter-pills">
                            <button className={filter === 'ALL' ? 'active' : ''} onClick={() => setFilter('ALL')}>All</button>
                            <button className={filter === 'NEW' ? 'active' : ''} onClick={() => setFilter('NEW')}>New</button>
                            <button className={filter === 'REPLIED' ? 'active' : ''} onClick={() => setFilter('REPLIED')}>Replied</button>
                        </div>
                    </div>

                    <div className="messages-list">
                        {loading ? (
                            <p className="loading-text">Loading messages...</p>
                        ) : (
                            getFilteredMessages().map(msg => (
                                <div
                                    key={msg.id}
                                    className={`message-item ${selectedMessage?.id === msg.id ? 'selected' : ''} ${msg.status === 'NEW' ? 'unread' : ''}`}
                                    onClick={() => setSelectedMessage(msg)}
                                >
                                    <div className="msg-avatar">
                                        <User size={20} />
                                    </div>
                                    <div className="msg-preview">
                                        <div className="msg-header-row">
                                            <h4>{msg.name}</h4>
                                            <span className="msg-time">
                                                {new Date(msg.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="msg-subject">{msg.subject}</p>
                                        <p className="msg-snippet">{msg.message.substring(0, 50)}...</p>
                                        {msg.status === 'REPLIED' && <div className="replied-badge"><CheckCircle size={12} weight="fill" /> Replied</div>}
                                    </div>
                                </div>
                            ))
                        )}
                        {!loading && getFilteredMessages().length === 0 && (
                            <div className="empty-state">No messages found</div>
                        )}
                    </div>
                </div>

                <div className="message-detail-view">
                    {selectedMessage ? (
                        <div className="detail-content">
                            <div className="detail-header">
                                <div className="detail-title">
                                    <h1>{selectedMessage.subject}</h1>
                                    <span className={`status-pill ${selectedMessage.status.toLowerCase()}`}>
                                        {selectedMessage.status}
                                    </span>
                                </div>
                                <div className="sender-info">
                                    <div className="sender-avatar-large">
                                        {selectedMessage.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3>{selectedMessage.name} <span className="email-text">&lt;{selectedMessage.email}&gt;</span></h3>
                                        <span className="timestamp">
                                            <Clock size={14} /> {new Date(selectedMessage.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="message-body">
                                {selectedMessage.message}
                            </div>

                            <div className="action-area">
                                {selectedMessage.status === 'REPLIED' ? (
                                    <div className="response-box">
                                        <h4><PaperPlaneRight size={18} /> You replied:</h4>
                                        <p>{selectedMessage.response}</p>
                                        <span className="response-time">
                                            Sent on {new Date(selectedMessage.responded_at).toLocaleString()}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="reply-composer">
                                        <h4>Reply to {selectedMessage.name}</h4>
                                        <textarea
                                            placeholder="Type your response here..."
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            rows={6}
                                        />
                                        <div className="composer-footer">
                                            <button
                                                className="btn-send"
                                                onClick={handleReply}
                                                disabled={isSending || !replyText.trim()}
                                            >
                                                {isSending ? 'Sending...' : 'Send Reply'} <PaperPlaneRight weight="bold" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="no-selection">
                            <Envelope size={64} weight="duotone" />
                            <h3>Select a message to view details</h3>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminMessagesPage;
